// graphql/operations.ts
import { gql } from '@apollo/client';

// ==================== TIMESHEET OPERATIONS ====================

export const GET_MY_TIMESHEET = gql`
  query GetMyTimesheet($weekStart: String!) {
    getMyTimesheet(weekStart: $weekStart) {
      id
      employeeId
      employeeName
      weekStart
      weekEnd
      totalHours
      status
      entries {
        id
        date
        startTime
        endTime
        breakMinutes
        totalHours
        status
      }
      createdAt
      updatedAt
    }
  }
`;

export const LIST_MY_TIMESHEETS = gql`
  query ListMyTimesheets($limit: Int, $nextToken: String) {
    listMyTimesheets(limit: $limit, nextToken: $nextToken) {
      items {
        id
        employeeId
        weekStart
        weekEnd
        totalHours
        status
        createdAt
      }
      nextToken
    }
  }
`;

export const LIST_ALL_TIMESHEETS = gql`
  query ListAllTimesheets($status: TimesheetStatus, $weekStart: String, $limit: Int, $nextToken: String) {
    listAllTimesheets(status: $status, weekStart: $weekStart, limit: $limit, nextToken: $nextToken) {
      items {
        id
        employeeId
        employeeName
        weekStart
        weekEnd
        totalHours
        status
        createdAt
      }
      nextToken
    }
  }
`;

export const CREATE_TIMESHEET = gql`
  mutation CreateTimesheet($input: CreateTimesheetInput!) {
    createTimesheet(input: $input) {
      id
      employeeId
      weekStart
      weekEnd
      totalHours
      status
      entries {
        id
        date
        startTime
        endTime
        breakMinutes
        totalHours
        status
      }
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TIMESHEET_ENTRY = gql`
  mutation UpdateTimesheetEntry($timesheetId: ID!, $entryId: ID!, $input: UpdateTimesheetEntryInput!) {
    updateTimesheetEntry(timesheetId: $timesheetId, entryId: $entryId, input: $input) {
      id
      employeeId
      weekStart
      weekEnd
      totalHours
      status
      entries {
        id
        date
        startTime
        endTime
        breakMinutes
        totalHours
        status
      }
    }
  }
`;

export const DELETE_TIMESHEET_ENTRY = gql`
  mutation DeleteTimesheetEntry($timesheetId: ID!, $entryId: ID!) {
    deleteTimesheetEntry(timesheetId: $timesheetId, entryId: $entryId) {
      id
      employeeId
      weekStart
      weekEnd
      totalHours
      status
      entries {
        id
        date
        startTime
        endTime
        breakMinutes
        totalHours
        status
      }
    }
  }
`;

export const SUBMIT_TIMESHEET = gql`
  mutation SubmitTimesheet($timesheetId: ID!) {
    submitTimesheet(timesheetId: $timesheetId) {
      id
      status
    }
  }
`;

export const APPROVE_TIMESHEET = gql`
  mutation ApproveTimesheet($timesheetId: ID!) {
    approveTimesheet(timesheetId: $timesheetId) {
      id
      status
    }
  }
`;

export const REJECT_TIMESHEET = gql`
  mutation RejectTimesheet($timesheetId: ID!, $reason: String) {
    rejectTimesheet(timesheetId: $timesheetId, reason: $reason) {
      id
      status
    }
  }
`;

// ==================== PAYROLL OPERATIONS ====================

export const RUN_PAYROLL = gql`
  mutation RunPayroll($payPeriodStart: String!, $payPeriodEnd: String!) {
    runPayroll(payPeriodStart: $payPeriodStart, payPeriodEnd: $payPeriodEnd) {
      id
      payPeriodStart
      payPeriodEnd
      payDate
      status
      totalGross
      totalNet
      totalTaxes
      entries {
        id
        employeeId
        employeeName
        grossPay
        netPay
        hoursWorked
        payRate
        taxes {
          federal
          state
          socialSecurity
          medicare
        }
      }
      createdAt
    }
  }
`;

export const PROCESS_PAYROLL = gql`
  mutation ProcessPayroll($payrollRunId: ID!) {
    processPayroll(payrollRunId: $payrollRunId) {
      id
      status
    }
  }
`;

export const LIST_PAYROLL_RUNS = gql`
  query ListPayrollRuns($limit: Int, $nextToken: String, $status: PayrollStatus) {
    listPayrollRuns(limit: $limit, nextToken: $nextToken, status: $status) {
      items {
        id
        payPeriodStart
        payPeriodEnd
        payDate
        status
        totalGross
        totalNet
        totalTaxes
        createdAt
      }
      nextToken
    }
  }
`;

export const GET_PAYROLL_RUN = gql`
  query GetPayrollRun($id: ID!) {
    getPayrollRun(id: $id) {
      id
      payPeriodStart
      payPeriodEnd
      payDate
      status
      totalGross
      totalNet
      totalTaxes
      entries {
        id
        employeeId
        employeeName
        grossPay
        netPay
        hoursWorked
        payRate
        taxes {
          federal
          state
          socialSecurity
          medicare
        }
      }
      createdAt
    }
  }
`;

export const GET_MY_PAY_STUBS = gql`
  query GetMyPayStubs($limit: Int, $nextToken: String) {
    getMyPayStubs(limit: $limit, nextToken: $nextToken) {
      items {
        id
        employeeId
        employeeName
        payrollRunId
        payPeriod
        payDate
        grossPay
        netPay
        hoursWorked
        payRate
        taxes {
          federal
          state
          socialSecurity
          medicare
        }
        deductions
      }
      nextToken
    }
  }
`;

export const GET_EMPLOYEE_PAY_STUB = gql`
  query GetEmployeePayStub($employeeId: String!, $payrollRunId: String!) {
    getEmployeePayStub(employeeId: $employeeId, payrollRunId: $payrollRunId) {
      id
      employeeId
      employeeName
      payrollRunId
      payPeriod
      payDate
      grossPay
      netPay
      hoursWorked
      payRate
      taxes {
        federal
        state
        socialSecurity
        medicare
      }
      deductions
    }
  }
`;

// ==================== EMPLOYEE PAYROLL INFO OPERATIONS ====================

export const GET_EMPLOYEE_PAYROLL_INFO = gql`
  query GetEmployeePayrollInfo($employeeId: String!) {
    getEmployeePayrollInfo(employeeId: $employeeId) {
      employeeId
      payRate
      payType
      taxWithholdings {
        federal
        state
        socialSecurity
        medicare
      }
      paymentMethod
      bankInfo {
        accountNumber
        routingNumber
        bankName
      }
      isActive
    }
  }
`;

export const LIST_EMPLOYEE_PAYROLL_INFO = gql`
  query ListEmployeePayrollInfo($limit: Int, $nextToken: String) {
    listEmployeePayrollInfo(limit: $limit, nextToken: $nextToken) {
      items {
        employeeId
        payRate
        payType
        taxWithholdings {
          federal
          state
          socialSecurity
          medicare
        }
        paymentMethod
        bankInfo {
          accountNumber
          routingNumber
          bankName
        }
        isActive
      }
      nextToken
    }
  }
`;

export const CREATE_EMPLOYEE_PAYROLL_INFO = gql`
  mutation CreateEmployeePayrollInfo($employeeId: String!, $input: EmployeePayrollInfoInput!) {
    createEmployeePayrollInfo(employeeId: $employeeId, input: $input) {
      employeeId
      payRate
      payType
      taxWithholdings {
        federal
        state
        socialSecurity
        medicare
      }
      paymentMethod
      bankInfo {
        accountNumber
        routingNumber
        bankName
      }
      isActive
    }
  }
`;

export const UPDATE_EMPLOYEE_PAYROLL_INFO = gql`
  mutation UpdateEmployeePayrollInfo($employeeId: String!, $input: EmployeePayrollInfoInput!) {
    updateEmployeePayrollInfo(employeeId: $employeeId, input: $input) {
      employeeId
      payRate
      payType
      taxWithholdings {
        federal
        state
        socialSecurity
        medicare
      }
      paymentMethod
      bankInfo {
        accountNumber
        routingNumber
        bankName
      }
      isActive
    }
  }
`;